import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useReviewMode } from "../context/ReviewModeContext";

import { useMsal } from "@azure/msal-react";

import {
  FileText,
  Mail,
  Pencil,
  Clock3,
  Files,
  Eye,
  RefreshCw,
} from "lucide-react";

import { callApi } from "../api/api";

import MetadataWorkspacePanel from "../components/MetadataWorkspacePanel";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";

export default function MetadataReview() {
  const { instance, accounts } = useMsal();

  const FILESYSTEM =
    process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";
  const { reviewMode, setReviewMode } = useReviewMode();

  const isSmallScreen = useBreakpoint(480);
  /* =========================================
     STATE
  ========================================= */

  const [activeTab, setActiveTab] = useState("pending");
  const [senderEmail, setSenderEmail] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [appliedSenderEmail, setAppliedSenderEmail] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  /* ---------- PENDING ---------- */

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const pendingPageSize = 10;

  /* ---------- VERIFIED ---------- */

  const [verifiedRows, setVerifiedRows] = useState([]);

  const [verifiedLoading, setVerifiedLoading] = useState(false);

  const [verifiedPage, setVerifiedPage] = useState(1);

  const [verifiedTotal, setVerifiedTotal] = useState(0);

  const [expandedRows, setExpandedRows] = useState({});

  const [previewLoading, setPreviewLoading] = useState(false);

  const verifiedPageSize = 10;

  /* ---------- COMMON ---------- */

  const [selectedRow, setSelectedRow] = useState(null);

  const [toast, setToast] = useState(null);
  const [isCompactTable, setIsCompactTable] = useState(false);
  const isNarrow = useBreakpoint(1024);
  const queueTopRef = useRef(null);
  const skipInitialPageScroll = useRef(true);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });

    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactTable(window.innerWidth <= 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* =========================================
     LOAD PENDING DATA
  ========================================= */

  const loadData = useCallback(
    async (signal) => {
      /*
      Prevent duplicate concurrent calls
    */

      try {
        setLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/pending",
          null,
          {
            signal,
            params: {
              page,
              pageSize: pendingPageSize,
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
            },
          },
        );

        /*
        Ignore aborted requests
      */

        if (signal?.aborted) return;

        setRows(res?.data?.data || []);

        setTotal(res?.data?.total || 0);
      } catch (err) {
        /*
        Ignore cancellation errors
      */

        if (
          err?.message === "Request cancelled" ||
          err?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Failed to load metadata review", err);

        setRows([]);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [
      instance,
      accounts,
      page,
      appliedSenderEmail,
      appliedFromDate,
      appliedToDate,
    ],
  );

  /* =========================================
     LOAD VERIFIED DATA
  ========================================= */

  const loadVerifiedData = useCallback(
    async (signal) => {
      /*
      Prevent duplicate concurrent calls
    */

      try {
        setVerifiedLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/verified",
          null,
          {
            signal,
            params: {
              page: verifiedPage,
              pageSize: verifiedPageSize,
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
            },
          },
        );

        /*
        Ignore aborted requests
      */

        if (signal?.aborted) return;

        setVerifiedRows(res?.data?.data || []);

        setVerifiedTotal(res?.data?.total || 0);
      } catch (err) {
        /*
        Ignore cancellation errors
      */

        if (
          err?.message === "Request cancelled" ||
          err?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Failed to load verified metadata", err);

        setVerifiedRows([]);
      } finally {
        if (!signal?.aborted) {
          setVerifiedLoading(false);
        }
      }
    },
    [
      instance,
      accounts,
      verifiedPage,
      appliedSenderEmail,
      appliedFromDate,
      appliedToDate,
    ],
  );

  /* =========================================
     EFFECTS
  ========================================= */

  useEffect(() => {
    if (!accounts.length || activeTab !== "pending") {
      return;
    }

    const controller = new AbortController();

    loadData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accounts.length, activeTab, loadData]);

  const handleSearch = () => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      showToast("From Date cannot be after To Date", "error");
      return;
    }

    setPage(1);
    setVerifiedPage(1);

    setAppliedSenderEmail(senderEmail.trim());
    setAppliedFromDate(fromDate ? new Date(fromDate).toISOString() : "");

    setAppliedToDate(toDate ? new Date(toDate).toISOString() : "");
  };

  useEffect(() => {
    if (!accounts.length || activeTab !== "verified") {
      return;
    }

    const controller = new AbortController();

    loadVerifiedData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accounts.length, activeTab, loadVerifiedData]);

  useEffect(() => {
    setSelectedRow(null);
  }, [activeTab]);

  useEffect(() => {
    setSelectedRow(null);
  }, [page, verifiedPage]);

  const scrollToQueueTop = useCallback(() => {
    const target = queueTopRef.current;
    if (!target || reviewMode) return;

    const scrollParent = target.closest(".app-shell__content");
    if (scrollParent) {
      const top =
        target.getBoundingClientRect().top -
        scrollParent.getBoundingClientRect().top +
        scrollParent.scrollTop;
      scrollParent.scrollTo({ top, behavior: "auto" });
      return;
    }

    target.scrollIntoView({ block: "start" });
  }, [reviewMode]);

  useEffect(() => {
    if (skipInitialPageScroll.current) {
      skipInitialPageScroll.current = false;
      return;
    }

    scrollToQueueTop();
  }, [page, verifiedPage, scrollToQueueTop]);

  useEffect(() => {
    setSelectedRow(null);
  }, [appliedSenderEmail, appliedFromDate, appliedToDate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [reviewMode]);

  /* =========================================
     GROUP PENDING ATTACHMENTS
  ========================================= */
  const getFileNameFromPath = (path) => {
    if (!path) return "";

    const parts = path.split("/");

    return parts[parts.length - 1];
  };
  const grouped = useMemo(() => {
    const map = {};

    rows.forEach((r) => {
      if (!map[r.msg_id]) {
        map[r.msg_id] = {
          ...r,
          attachments: [],
        };

        /*
      Add email (.eml) as first attachment
    */
        if (r.email_storage_path) {
          map[r.msg_id].attachments.push({
            name: getFileNameFromPath(r.email_storage_path),
            path: r.email_storage_path,
            type: "email",
          });
        }
      }

      if (r.attachment_name) {
        map[r.msg_id].attachments.push({
          name: r.attachment_name,
          path: r.attachment_storage_path,
          type: "attachment",
        });
      }
    });

    return Object.values(map);
  }, [rows]);

  /* =========================================
     GROUP VERIFIED ATTACHMENTS
  ========================================= */

  const verifiedGrouped = useMemo(() => {
    const map = {};

    verifiedRows.forEach((r) => {
      if (!map[r.msg_id]) {
        map[r.msg_id] = {
          ...r,
          attachments: [],
        };

        if (r.email_storage_path) {
          map[r.msg_id].attachments.push({
            name: getFileNameFromPath(r.email_storage_path),
            path: r.email_storage_path,
            type: "email",
          });
        }
      }

      if (r.attachment_name) {
        map[r.msg_id].attachments.push({
          name: r.attachment_name,
          path: r.attachment_storage_path,
          type: "attachment",
        });
      }
    });

    return Object.values(map);
  }, [verifiedRows]);

  /* =========================================
     HELPERS
  ========================================= */

  const formatDate = useCallback((date) => {
    if (!date) return "—";

    return new Date(date).toLocaleString();
  }, []);

  const totalPages = Math.ceil(total / pendingPageSize);

  const verifiedTotalPages = Math.ceil(verifiedTotal / verifiedPageSize);

  /* =========================================
     ACTIVE COUNT
  ========================================= */

  const activeCount = activeTab === "pending" ? total : verifiedTotal;
  const activeRows = activeTab === "pending" ? grouped : verifiedGrouped;
  const currentIndex = activeRows.findIndex(
    (x) => x.msg_id === selectedRow?.msg_id,
  );
  const handlePreview = useCallback(async (path) => {
    /*
    Prevent preview spam / double clicks
  */

    if (previewLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setPreviewLoading(true);

      const cleaned = path?.trim();

      if (!cleaned) {
        return;
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const lower = filePath.toLowerCase();

      const isTiff = lower.endsWith(".tif") || lower.endsWith(".tiff");

      const isEml = lower.endsWith(".eml");

      const isHeic = lower.endsWith(".heic") || lower.endsWith(".heif");

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      const BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_CLIENT_ID;

      /* =========================
       TIFF PREVIEW
    ========================= */

      if (isTiff || isEml || isHeic) {
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${BACKEND_CLIENT_ID}/access_as_user`],
        });

        const response = await fetch(
          `${baseUrl}/api/adls/preview` +
            `?filesystem=${encodeURIComponent(filesystem)}` +
            `&path=${encodeURIComponent(filePath)}`,
          {
            method: "GET",
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to preview file (${response.status})`);
        }

        const blob = await response.blob();

        if (!blob || blob.size === 0) {
          throw new Error("Empty preview response");
        }

        const blobUrl = URL.createObjectURL(blob);

        window.open(blobUrl, "_blank", "noopener,noreferrer");

        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000);

        return;
      }
      /* =========================
       NORMAL FILES
    ========================= */

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/adls/preview-url",
        null,
        {
          signal: controller.signal,

          params: {
            filesystem,
            path: filePath,
          },
        },
      );

      if (!res?.data?.url) {
        throw new Error("Preview URL missing");
      }

      /*
      Safe window open
    */

      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      /*
      Ignore cancellation
    */

      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error("Preview failed", err);

      showToast?.(err?.message || "Failed to preview document", "error");
    } finally {
      controller.abort();

      setPreviewLoading(false);
    }
  }, [accounts, instance, previewLoading, showToast, FILESYSTEM]);

  const activeLabel =
    activeTab === "pending" ? "Pending Records" : "Verified Records";

  /* =========================================
     RENDER TABLE
  ========================================= */

  const handleReindex = useCallback(async (msgId) => {
    try {
      await callApi(
        instance,
        accounts,
        "PUT",
        `/api/metadata/reindex/${msgId}`,
      );

      showToast("Reindex triggered successfully", "success");

      loadVerifiedData();
    } catch (err) {
      console.error("Reindex failed", err);

      showToast("Failed to reindex metadata", "error");
    }
  }, [accounts, instance, loadVerifiedData, showToast]);

  const goNext = () => {
    if (currentIndex < activeRows.length - 1) {
      const nextRow = activeRows[currentIndex + 1];

      setSelectedRow(nextRow);

      const rowKey = `${activeTab}-${nextRow.msg_id}`;

      setExpandedRows((prev) => ({
        ...prev,
        [rowKey]: true,
      }));
    }
  };

  const goPrevious = () => {
    if (currentIndex > 0) {
      const prevRow = activeRows[currentIndex - 1];

      setSelectedRow(prevRow);

      const rowKey = `${activeTab}-${prevRow.msg_id}`;

      setExpandedRows((prev) => ({
        ...prev,
        [rowKey]: true,
      }));
    }
  };

  /* =========================================
     MOBILE CARD/DRAWER MAPPERS
  ========================================= */

  const mapPendingCard = useCallback(
    (row) => ({
      id: `pending-${row.msg_id}`,
      icon: <Mail size={16} />,
      tone: "info",
      title: row.email_subject || "No subject",
      subtitle: row.mail_from_address || "—",
      meta: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        { label: "Entry", value: formatDate(row.created_at) },
      ],
      badges: row.status
        ? [
            {
              label: "Status",
              value: row.status,
              tone: "success",
            },
          ]
        : undefined,
      attachmentsCount: row.attachments?.length ?? 0,
      actions: reviewMode
        ? undefined
        : [
            {
              label: "Edit",
              onClick: () => {
                setSelectedRow(row);
                setReviewMode(true);
                setExpandedRows((prev) => ({
                  ...prev,
                  [`pending-${row.msg_id}`]: true,
                }));
              },
              icon: <Pencil size={14} />,
            },
          ],
    }),
    [formatDate, reviewMode, setExpandedRows, setReviewMode, setSelectedRow],
  );

  const mapPendingDrawer = useCallback(
    (row) => ({
      title: row.email_subject || "No subject",
      fields: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        { label: "Sender", value: row.mail_from_address || "—" },
        { label: "Entry Date & Time", value: formatDate(row.created_at) },
        { label: "Subject", value: row.email_subject || "—" },
      ],
      attachments:
        row.attachments?.map((a) => ({
          label: a.name,
          onPreview: () => handlePreview(a.path),
        })) || [],
      primaryAction: reviewMode
        ? null
        : {
            label: "Edit",
            onClick: () => {
              setSelectedRow(row);
              setReviewMode(true);
              setExpandedRows((prev) => ({
                ...prev,
                [`pending-${row.msg_id}`]: true,
              }));
            },
          },
    }),
    [
      formatDate,
      handlePreview,
      reviewMode,
      setExpandedRows,
      setReviewMode,
      setSelectedRow,
    ],
  );

  const mapVerifiedCard = useCallback(
    (row) => ({
      id: `verified-${row.msg_id}`,
      icon: <Files size={16} />,
      tone: "success",
      title: row.email_subject || "No subject",
      subtitle: row.mail_from_address || "—",
      meta: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        { label: "Verified By", value: row.verified_by || row.modified_by || "System" },
        { label: "Entry", value: formatDate(row.created_at) },
      ],
      attachmentsCount: row.attachments?.length ?? 0,
      actions: [
        {
          label: "Reindex",
          variant: "secondary",
          onClick: () => handleReindex(row.msg_id),
          icon: <RefreshCw size={14} />,
        },
      ],
    }),
    [formatDate, handleReindex],
  );

  const mapVerifiedDrawer = useCallback(
    (row) => ({
      title: row.email_subject || "No subject",
      fields: [
        {
          label: "Metadata",
          value: `${row.document_type || "UNKNOWN"} • ${row.policy_number || "N/A"}`,
        },
        { label: "Sender", value: row.mail_from_address || "—" },
        {
          label: "Verified By",
          value: row.verified_by || row.modified_by || row.updated_by || "System",
        },
        { label: "Entry Date & Time", value: formatDate(row.created_at) },
        { label: "Subject", value: row.email_subject || "—" },
      ],
      attachments:
        row.attachments?.map((a) => ({
          label: a.name,
          onPreview: () => handlePreview(a.path),
        })) || [],
      primaryAction: {
        label: "Reindex",
        onClick: () => handleReindex(row.msg_id),
      },
    }),
    [formatDate, handlePreview, handleReindex],
  );

  const renderTable = (
    data,
    isLoading,
    currentPage,
    currentTotalPages,
    currentTotal,
    emptyTitle,
    emptySub,
    verified = false,
  ) => (
    <>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.cardTitle}>
            <Files size={18} />

            {verified ? "Verified Metadata Queue" : "Pending Metadata Queue"}
          </div>

        </div>

        <ScrollableTable style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                  Metadata
                </th>

                <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                  Email Subject
                </th>

                <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                  Sender
                </th>

                <th style={{ ...styles.th, ...(isCompactTable ? styles.thCompact : {}) }}>
                  {verified ? "Verified By" : "Attachments"}
                </th>

                {!reviewMode && <th style={{ ...styles.thRight, ...(isCompactTable ? styles.thRightCompact : {}) }}>
                  Action
                </th>}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" style={styles.empty}>
                    <div style={styles.loaderContainer}>
                      <div style={styles.loader} />

                      <span>Loading metadata...</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="5" style={styles.empty}>
                    <div style={styles.emptyState}>
                      <Files size={42} strokeWidth={1.5} />

                      <div style={styles.emptyTitle}>{emptyTitle}</div>

                      <div style={styles.emptySub}>{emptySub}</div>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((row) => {
                  const rowKey = `${verified ? "verified" : "pending"}-${row.msg_id}`;

                  const isExpanded = reviewMode || expandedRows[rowKey];

                  return (
                    <React.Fragment key={rowKey}>
                      {/* ================= MAIN ROW ================= */}

                      <tr style={styles.row}>
                        <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                          <div style={styles.metaBlock}>
                            <div style={styles.metaTitle}>
                              {row.document_type || "UNKNOWN"}

                              <span style={styles.dot}>•</span>

                              {row.policy_number || "N/A"}
                            </div>

                            <div style={styles.metaSub}>
                              <Clock3 size={13} />
                              Entry: {formatDate(row.created_at)}
                            </div>
                          </div>
                        </td>

                        <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                          <div style={{ ...styles.subject, ...(isCompactTable ? styles.subjectCompact : {}) }}>
                            <span style={styles.subjectIcon}>
                              <Mail size={14} />
                            </span>

                            <span style={{ ...styles.subjectText, ...(isCompactTable ? styles.subjectTextCompact : {}) }}>
                              {row.email_subject || "—"}
                            </span>
                          </div>
                        </td>

                        <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                          <div style={{ ...styles.sender, ...(isCompactTable ? styles.senderCompact : {}) }}>
                            {row.mail_from_address || "—"}
                          </div>
                        </td>

                        {/* ================= VERIFIED ================= */}

                        {verified ? (
                          <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                            <div style={styles.sender}>
                              {row.verified_by ||
                                row.modified_by ||
                                row.updated_by ||
                                "System"}
                            </div>
                          </td>
                        ) : (
                          <td style={{ ...styles.td, ...(isCompactTable ? styles.tdCompact : {}) }}>
                            {row.attachments.length === 0 ? (
                              <span style={styles.noAttachment}>
                                No attachments
                              </span>
                            ) : (
                              <button
                                style={styles.attachmentCountBtn}
                                onClick={() =>
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [rowKey]: !prev[rowKey],
                                  }))
                                }
                              >
                                <FileText size={14} />
                                {row.attachments.length}
                                {row.attachments.length > 1 ? "" : ""}
                              </button>
                            )}
                          </td>
                        )}

                        {/* ================= ACTION ================= */}

                        <td style={{ ...styles.tdRight, ...(isCompactTable ? styles.tdRightCompact : {}) }}>
                          {verified ? (
                            <button
                              title="ReIndex Metadata"
                              style={styles.updateBtn}
                              onClick={() => handleReindex(row.msg_id)}
                            >
                              <RefreshCw size={14} />
                            </button>
                          ) : !reviewMode ? (
                            <button
                              title="Edit and Verify Metadata"
                              style={styles.updateBtn}
                              onClick={() => {
                                setSelectedRow(row);

                                setReviewMode(true);

                                setExpandedRows((prev) => ({
                                  ...prev,
                                  [rowKey]: true,
                                }));
                              }}
                            >
                              <Pencil size={14} />
                            </button>
                          ) : null}
                        </td>
                      </tr>

                      {/* ================= ATTACHMENTS ROW ================= */}

                      {!verified &&
                        isExpanded &&
                        row.attachments.length > 0 && (
                          <tr>
                            <td colSpan="5" style={styles.attachmentRowTd}>
                              <div style={styles.attachmentPanel}>
                                {row.attachments.map((a, idx) => (
                                  <div key={idx} style={styles.attachmentCard}>
                                    <div style={styles.attachmentCardLeft}>
                                      <FileText size={16} />

                                      <div style={styles.attachmentDetails}>
                                        <div style={styles.attachmentName}>
                                          {a.name}
                                        </div>
                                      </div>
                                    </div>

                                    <button
                                      title="Preview Document"
                                      style={styles.previewBtn}
                                      onClick={() => handlePreview(a.path)}
                                    >
                                      <Eye size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </ScrollableTable>
      </div>

      {!reviewMode && !isLoading && currentTotal > 0 && (
        <Pagination
          page={currentPage}
          totalPages={currentTotalPages || 1}
          totalRecords={currentTotal}
          pageSize={verified ? verifiedPageSize : pendingPageSize}
          currentPageCount={data.length}
          onPageChange={(nextPage) =>
            verified ? setVerifiedPage(nextPage) : setPage(nextPage)
          }
        />
      )}
    </>
  );

  const renderResponsiveQueue = (
    data,
    isLoading,
    currentPage,
    currentTotalPages,
    currentTotal,
    emptyTitle,
    emptySub,
    verified = false,
  ) => (
    <ResponsiveTableCards
      breakpoint={1024}
      data={data}
      loading={isLoading}
      emptyTitle={emptyTitle}
      emptySub={emptySub}
      listTitle={verified ? "Verified Metadata Queue" : "Pending Metadata Queue"}
      listSubtitle={
        verified ? "Recently verified records" : "Quick scan of all items"
      }
      cardMapper={verified ? mapVerifiedCard : mapPendingCard}
      drawerMapper={verified ? mapVerifiedDrawer : mapPendingDrawer}
      pagination={
        currentTotal > 0
          ? {
              page: currentPage,
              totalPages: currentTotalPages || 1,
              totalRecords: currentTotal,
              pageSize: verified ? verifiedPageSize : pendingPageSize,
              currentPageCount: data.length,
              onPageChange: (nextPage) =>
                verified ? setVerifiedPage(nextPage) : setPage(nextPage),
            }
          : undefined
      }
      renderTable={() =>
        renderTable(
          data,
          isLoading,
          currentPage,
          currentTotalPages,
          currentTotal,
          emptyTitle,
          emptySub,
          verified,
        )
      }
    />
  );

  /* =========================================
     UI
  ========================================= */

  return (
    <div
      style={{
        ...styles.container,
        ...(isSmallScreen ? styles.containerNarrow : {}),
        ...(reviewMode ? styles.reviewContainerMode : {}),
      }}
    >
      {!reviewMode && (
        <div style={{ ...styles.header, ...(isSmallScreen ? styles.headerNarrow : {}) }}>
          <div>
            <h2 style={styles.title}>Metadata Review</h2>

            <p style={styles.sub}>
              Review, validate, and update pending document metadata before
              verification.
            </p>
          </div>

          <div style={{ ...styles.statsCard, ...(isSmallScreen ? styles.statsCardNarrow : {}) }}>
            <div style={{ ...styles.statsValue, ...(isSmallScreen ? styles.statsValueNarrow : {}) }}>
              {activeCount}
            </div>

            <div style={{ ...styles.statsLabel, ...(isSmallScreen ? styles.statsLabelNarrow : {}) }}>
              {activeLabel}
            </div>
          </div>
        </div>
      )}

      {!reviewMode && (
        <div style={{ ...styles.tabs, ...(isSmallScreen ? styles.tabsNarrow : {}) }}>
          <button
            style={{
              ...styles.tabBtn,
              ...(isSmallScreen ? styles.tabBtnNarrow : {}),

              ...(activeTab === "pending" ? styles.activeTabBtn : {}),
            }}
            onClick={() => setActiveTab("pending")}
          >
            Pending Review
          </button>

          <button
            style={{
              ...styles.tabBtn,
              ...(isSmallScreen ? styles.tabBtnNarrow : {}),

              ...(activeTab === "verified" ? styles.activeTabBtn : {}),
            }}
            onClick={() => setActiveTab("verified")}
          >
            Verified Records
          </button>
        </div>
      )}

      {!reviewMode && (
        <div style={styles.filterCard}>
          <div style={styles.filterGrid}>
            <div style={styles.filterField}>
              <label style={styles.filterLabel}>Sender Email</label>

              <input
                type="text"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="Search sender email"
                style={styles.filterInput}
              />
            </div>

            <div style={styles.filterField}>
              <label style={styles.filterLabel}>From Date & Time</label>

              <input
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={styles.filterInput}
              />
            </div>

            <div style={styles.filterField}>
              <label style={styles.filterLabel}>To Date & Time</label>

              <input
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={styles.filterInput}
              />
            </div>

            <div style={styles.filterActions}>
              <button style={styles.searchBtn} onClick={handleSearch}>
                Search
              </button>

              <button
                style={styles.clearBtn}
                onClick={() => {
                  setSenderEmail("");
                  setFromDate("");
                  setToDate("");

                  setAppliedSenderEmail("");
                  setAppliedFromDate("");
                  setAppliedToDate("");

                  setPage(1);
                  setVerifiedPage(1);
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {!reviewMode && activeTab === "pending" && (
        <div ref={queueTopRef}>
          {renderResponsiveQueue(
            grouped,
            loading,
            page,
            totalPages,
            total,
            "No Pending Metadata",
            "All records are already reviewed and verified.",
            false,
          )}
        </div>
      )}

      {!reviewMode && activeTab === "verified" && (
        <div ref={queueTopRef}>
          {renderResponsiveQueue(
            verifiedGrouped,
            verifiedLoading,
            verifiedPage,
            verifiedTotalPages,
            verifiedTotal,
            "No Verified Metadata",
            "No verified records found.",
            true,
          )}
        </div>
      )}
      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
          }}
        >
          {toast.message}
        </div>
      )}

      {reviewMode && selectedRow && (
        <div style={styles.reviewWorkspace}>
          {/* =====================================
        REVIEW HEADER
    ===================================== */}

          {/* =====================================
        REVIEW CONTENT
    ===================================== */}

          <div
            style={{
              ...styles.reviewContent,
              ...(isNarrow ? styles.reviewContentNarrow : null),
              ...(isSmallScreen ? styles.reviewContentSmall : null),
            }}
          >
            {/* LEFT SIDE */}

            <div
              style={{
                ...styles.reviewMain,
                ...(isNarrow ? styles.reviewMainNarrow : null),
                ...(isSmallScreen ? styles.reviewMainSmall : null),
              }}
              className="scrollable"
            >
              <ResponsiveTableCards
                breakpoint={1024}
                data={[selectedRow]}
                loading={false}
                emptyTitle="No record selected"
                emptySub=""
                listTitle={
                  activeTab === "verified"
                    ? "Verified Metadata Queue"
                    : "Pending Metadata Queue"
                }
                listSubtitle="Selected record for review"
                cardMapper={
                  activeTab === "verified" ? mapVerifiedCard : mapPendingCard
                }
                drawerMapper={
                  activeTab === "verified" ? mapVerifiedDrawer : mapPendingDrawer
                }
                renderTable={() =>
                  renderTable(
                    [selectedRow],
                    false,
                    1,
                    1,
                    1,
                    "",
                    "",
                    activeTab === "verified",
                  )
                }
              />
            </div>

            {/* RIGHT SIDE PANEL */}

            <div
              style={{
                ...styles.workspaceLayout,
                ...(isNarrow ? styles.workspaceLayoutNarrow : null),
                ...(isSmallScreen ? styles.workspaceLayoutSmall : null),
              }}
            >
              <MetadataWorkspacePanel
                row={selectedRow}
                activeTab={activeTab}
                onClose={() => {
                  setReviewMode(false);
                  setSelectedRow(null);
                  setExpandedRows({});
                }}
                onPreview={handlePreview}
                previewLoading={previewLoading}
                onReindex={handleReindex}
                onSaved={() => {
                  const controller = new AbortController();

                  if (activeTab === "pending") {
                    loadData(controller.signal);
                  } else {
                    loadVerifiedData(controller.signal);
                  }
                }}
              />
            </div>
          </div>

          <div style={{ ...styles.reviewActionBar, ...(isNarrow ? styles.reviewActionBarNarrow : null) }}>
            {/* LEFT */}

            <button
              style={{
                ...styles.reviewNavBtn,
                opacity: currentIndex === 0 ? 0.5 : 1,
                cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              }}
              disabled={currentIndex === 0}
              onClick={goPrevious}
            >
              Previous
            </button>

            {/* RIGHT */}

            <button
              style={{
                ...styles.reviewNavBtn,
                opacity: currentIndex === activeRows.length - 1 ? 0.5 : 1,
                cursor:
                  currentIndex === activeRows.length - 1
                    ? "not-allowed"
                    : "pointer",
              }}
              disabled={currentIndex === activeRows.length - 1}
              onClick={goNext}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================
   STYLES
========================================= */

const styles = {
  reviewContainerMode: {
    height: "100vh",
    overflow: "hidden",
    padding: 0,
  },
  reviewWorkspace: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden",
  },

  reviewNavBtn: {
    height: "46px",
    minWidth: "130px",
    padding: "0 22px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: "600",
  },

  exitBtn: {
    height: "40px",
    padding: "0 18px",
    borderRadius: "10px",
    border: "none",
    background: "#dc2626",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: "600",
  },

  reviewContent: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    minHeight: 0,
  },

  reviewContentNarrow: {
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "14px 16px",
    gap: "14px",
    boxSizing: "border-box",
  },

  reviewContentSmall: {
    padding: "10px 12px",
    gap: "10px",
  },

  reviewMain: {
    flex: 1,
    overflowY: "auto",
    padding: "14px",
    minHeight: 0,
  },

  reviewMainNarrow: {
    flex: "none",
    overflowY: "auto",
    maxHeight: "42vh",
    padding: 0,
    minHeight: 0,
  },

  reviewMainSmall: {
    maxHeight: "38vh",
  },

  reviewFooter: {
    height: "72px",
    background: "#ffffff",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    flexShrink: 0,
  },

  reviewFooterLeft: {
    display: "flex",
    gap: "12px",
  },
  reviewActionBar: {
    height: "84px",
    background: "#ffffff",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 32px",
    flexShrink: 0,
  },

  reviewActionBarNarrow: {
    height: "64px",
    padding: "0 16px",
  },

  reviewCenterButtons: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  cancelReviewBtn: {
    height: "46px",
    minWidth: "140px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    fontWeight: "600",
    cursor: "pointer",
  },

  updateReviewBtn: {
    height: "46px",
    minWidth: "160px",
    borderRadius: "12px",
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
    fontWeight: "700",
    cursor: "pointer",
  },
  workspaceLayout: {
    width: "480px",
    minWidth: "480px",
    maxWidth: "480px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    boxShadow: "0 1px 4px rgba(15,23,42,0.07)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: "1rem",
    alignSelf: "flex-start",
    maxHeight: "calc(100% - 2rem)",
  },

  workspaceLayoutNarrow: {
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    margin: 0,
    alignSelf: "stretch",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    boxShadow: "0 1px 4px rgba(15, 23, 42, 0.07)",
    flex: "1 1 auto",
    overflow: "hidden",
    maxHeight: "none",
  },

  workspaceLayoutSmall: {
    borderRadius: "14px",
  },
  searchBtn: {
    height: "42px",
    padding: "10px 16px",
    border: "none",
    borderRadius: "10px",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "600",
  },
  container: {
    padding: "24px",
    background: "#f8fafc",
    minHeight: "80vh",
  },
  containerNarrow: {
    padding: "12px 12px 28px",
  },
  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 9999,
  },
  attachmentCountBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "#eff6ff",
    color: "#2563eb",
    border: "1px solid #bfdbfe",
    borderRadius: "10px",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },

  attachmentRowTd: {
    padding: "0",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  attachmentPanel: {
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  attachmentCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 14px",
  },

  attachmentCardLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },

  attachmentDetails: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  attachmentName: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
    wordBreak: "break-word",
  },

  attachmentPath: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px",
    wordBreak: "break-all",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    gap: "20px",
  },
  headerNarrow: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  tabs: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
  },
  tabsNarrow: {
    flexWrap: "wrap",
    marginBottom: "20px",
    width: "100%",
  },

  tabBtn: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#475569",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  tabBtnNarrow: {
    flex: "1 1 48%",
    minWidth: 0,
    textAlign: "center",
  },

  activeTabBtn: {
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
  },

  statsCard: {
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: "14px",
    padding: "14px 20px",
    minWidth: "160px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  statsCardNarrow: {
    minWidth: "100%",
    width: "100%",
    textAlign: "left",
    padding: "12px 14px",
    boxSizing: "border-box",
  },

  statsValue: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#2563eb",
  },
  statsValueNarrow: {
    fontSize: "24px",
  },

  statsLabel: {
    fontSize: "13px",
    color: "#64748b",
    marginTop: "4px",
  },
  statsLabelNarrow: {
    fontSize: "12px",
    marginTop: "2px",
  },

  card: {
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 20px",
    borderBottom: "1px solid #e2e8f0",
  },

  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: "600",
    color: "#0f172a",
  },

  pageInfo: {
    fontSize: "13px",
    color: "#64748b",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "auto",
  },

  th: {
    textAlign: "left",
    whiteSpace: "nowrap",
    background: "#f8fafc",
    padding: "14px 18px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },

  thRight: {
    textAlign: "center",
    background: "#f8fafc",
    padding: "14px 18px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },

  row: {
    transition: "background 0.2s ease",
  },

  td: {
    padding: "14px 18px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    overflow: "hidden",
    fontSize: "13px",
    textAlign: "left",
  },
  thCompact: {
    padding: "10px 12px",
    fontSize: "11px",
  },
  thRightCompact: {
    padding: "10px 12px",
    fontSize: "11px",
  },
  tdCompact: {
    padding: "12px 12px",
    fontSize: "13px",
    textAlign: "left",
  },
  metaTitle: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    overflowWrap: "anywhere",
  },

  tdRight: {
    padding: "18px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    textAlign: "center",
  },

  metaBlock: {
    minWidth: "220px",
    maxWidth: "320px",
    overflow: "hidden",
  },

  dot: {
    color: "#94a3b8",
  },

  metaSub: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#64748b",
    fontSize: "13px",
    marginTop: "8px",
  },

  subject: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    // lineHeight: "1.5", //no lineHeight here 
    minWidth: 0,
    maxWidth: "340px",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  
  subjectIcon:{
    display:'flex',
    flexShrink:0,
    marginTop:'2px',
  },

  subjectText:{
    lineHeight:1.4,
    minWidth:0,
    fontSize:'13px',
  },
  subjectCompact: {
    gap: "6px",
  },
  subjectTextCompact: {
    fontSize: "13px",
  },


  sender: {
    color: "#334155",
    wordBreak: "break-word",
    maxWidth: "180px",
  },
  senderCompact: {
    fontSize: "13px",
  },

  noAttachment: {
    color: "#94a3b8",
    fontSize: "13px",
  },

  attachmentRail: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    overflowX: "auto",
    overflowY: "hidden",
    maxWidth: "320px",
    paddingBottom: "2px",
    scrollbarWidth: "thin",
  },
  attachmentRailCompact: {
    gap: "6px",
    paddingBottom: "1px",
  },

  attachmentChip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    minWidth: "160px",
    maxWidth: "160px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "6px 8px",
    flexShrink: 0,
  },

  attachmentChipLeft: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    overflow: "hidden",
    flex: 1,
    color: "#334155",
  },

  attachmentChipText: {
    fontSize: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  previewBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    border: "none",
    background: "#e0e7ff",
    color: "#2563eb",
    cursor: "pointer",
    flexShrink: 0,
  },

  updateBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0f172a",
    color: "#ffffff",
    border: "none",
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    cursor: "pointer",
  },

  empty: {
    padding: "60px 20px",
    textAlign: "center",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    color: "#94a3b8",
  },

  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#334155",
  },

  emptySub: {
    fontSize: "14px",
    color: "#64748b",
  },

  loaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: "#64748b",
  },

  loader: {
    width: "18px",
    height: "18px",
    border: "2px solid #dbeafe",
    borderTop: "2px solid #2563eb",
    borderRadius: "50%",
  },

  filterCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "20px",
  },

  filterGrid: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },

  filterField: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 200px",
    minWidth: 0,
  },

  filterLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    textTransform: "uppercase",
  },

  filterInput: {
    height: "42px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    padding: "0 12px",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
  },

  filterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: "10px",
    flex: "0 0 auto",
    marginLeft: "auto",
  },

  clearBtn: {
    height: "42px",
    padding: "0 16px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    background: "#fff",
    cursor: "pointer",
    fontWeight: "600",
  },
};
